/**
 * AdditionalsContent - Gestão de adicionais (extras) dos produtos
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusCircle, Plus, Eye, EyeOff, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const AdditionalsContent = () => {
  const [additionals, setAdditionals] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdditional, setEditingAdditional] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    group_ids: [],
    available: true
  });

  useEffect(() => {
    fetchAdditionals();
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchAdditionals = async () => {
    try {
      const response = await axios.get(`${API}/additionals`);
      setAdditionals(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching additionals:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const additionalData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        group_ids: formData.group_ids || [],
        available: formData.available
      };

      if (editingAdditional) {
        await axios.put(`${API}/additionals/${editingAdditional.id}`, additionalData);
      } else {
        await axios.post(`${API}/additionals`, additionalData);
      }
      
      setIsModalOpen(false);
      setEditingAdditional(null);
      setFormData({ name: '', description: '', price: '', group_ids: [], available: true });
      fetchAdditionals();
      fetchGroups(); // Refresh group counts
    } catch (error) {
      console.error('Error saving additional:', error);
    }
  };

  const handleEdit = (additional) => {
    setEditingAdditional(additional);
    // Handle both new group_ids and legacy group_id
    let groupIds = additional.group_ids || [];
    if (groupIds.length === 0 && additional.group_id) {
      groupIds = [additional.group_id];
    }
    setFormData({
      name: additional.name || '',
      description: additional.description || '',
      price: additional.price != null ? additional.price.toString() : '',
      group_ids: groupIds,
      available: additional.available !== false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (additionalId) => {
    if (window.confirm('Tem certeza que deseja excluir este adicional?')) {
      try {
        await axios.delete(`${API}/additionals/${additionalId}`);
        fetchAdditionals();
        fetchGroups(); // Refresh group counts
      } catch (error) {
        console.error('Error deleting additional:', error);
      }
    }
  };

  const toggleAvailability = async (additionalId) => {
    try {
      await axios.patch(`${API}/additionals/${additionalId}/toggle-availability`);
      fetchAdditionals();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const openModal = () => {
    setEditingAdditional(null);
    setFormData({ name: '', description: '', price: '', group_ids: [], available: true });
    setIsModalOpen(true);
  };

  // Helper function to get group names for an additional
  const getGroupNames = (additional) => {
    const groupIds = additional.group_ids || (additional.group_id ? [additional.group_id] : []);
    return groupIds.map(gid => {
      const group = groups.find(g => g.id === gid);
      return group ? group.name : null;
    }).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Adicionais</h1>
        <button onClick={openModal} className="glass-button-primary">
          <PlusCircle size={18} className="mr-2" />
          Novo Adicional
        </button>
      </div>

      <div className="glass-card">
        {additionals.length > 0 ? (
          <div className="space-y-4">
            {additionals.map((additional) => {
              const groupNames = getGroupNames(additional);
              return (
              <div key={additional.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center space-x-4 min-w-0 flex-1">
                  <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <Plus size={20} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white break-words">{additional.name}</h3>
                    <p className="text-green-400 font-medium">R$ {additional.price.toFixed(2)}</p>
                    {groupNames.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {groupNames.map((name, idx) => (
                          <span key={idx} className="px-2 py-0.5 text-xs bg-cyan-500/20 text-cyan-300 rounded">
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">Sem grupo</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  <span className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
                    additional.available 
                      ? 'bg-green-400/20 text-green-400' 
                      : 'bg-red-400/20 text-red-400'
                  }`}>
                    {additional.available ? 'Disponível' : 'Indisponível'}
                  </span>
                  
                  <button
                    onClick={() => toggleAvailability(additional.id)}
                    className={`p-2 rounded-lg flex-shrink-0 transition-colors ${
                      additional.available 
                        ? 'text-green-400 hover:bg-green-400/20' 
                        : 'text-red-400 hover:bg-red-400/20'
                    }`}
                  >
                    {additional.available ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  
                  <button
                    onClick={() => handleEdit(additional)}
                    className="p-2 flex-shrink-0 text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(additional.id)}
                    className="p-2 flex-shrink-0 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Plus size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Nenhum adicional encontrado</h3>
            <p className="text-gray-400 mb-6">Adicione opções extras para seus produtos</p>
            <button onClick={openModal} className="glass-button-primary">
              Criar Primeiro Adicional
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingAdditional ? 'Editar Adicional' : 'Novo Adicional'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                required
                className="glass-input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Bacon, Cheddar, Molho especial"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <input
                type="text"
                className="glass-input w-full"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrição opcional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Grupos de Adicionais</label>
              {groups.length === 0 ? (
                <p className="text-gray-400 text-sm italic py-2">
                  Nenhum grupo criado. Crie grupos em &quot;Grupos de Adicionais&quot; primeiro.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-white/5 rounded-lg border border-white/10">
                  {groups.map(group => (
                    <label key={group.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(formData.group_ids || []).includes(group.id)}
                        onChange={(e) => {
                          const currentGroups = formData.group_ids || [];
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              group_ids: [...currentGroups, group.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              group_ids: currentGroups.filter(id => id !== group.id)
                            });
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-500"
                      />
                      <div className="flex-1">
                        <span className="text-white">{group.name}</span>
                        <span className="text-xs text-gray-400 ml-2">
                          ({group.additionals_count || 0} adicionais)
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                Selecione os grupos onde este adicional estará disponível. Pode selecionar múltiplos grupos.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Preço *</label>
              <input
                type="number"
                step="0.01"
                required
                className="glass-input w-full"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                placeholder="0.00"
              />
            </div>
            
            <div className="flex items-center space-x-3">
              <Switch 
                id="additional-available"
                checked={formData.available}
                onCheckedChange={(checked) => setFormData({...formData, available: checked})}
              />
              <Label htmlFor="additional-available" className="text-sm text-gray-300 cursor-pointer">
                Adicional disponível
              </Label>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingAdditional ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdditionalsContent;
